import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import {
  EatingWayAreaImageModal,
  EatingWayAreaImageModalProps,
  EatingWayDetailModal,
  EatingWayDetailModalProps,
} from 'features/EatingWay/components';
import eatingWayService from 'features/EatingWay/eatingWayService';
import { EatingWayGetAllPayload, EatingWayListResponse } from 'features/EatingWay/models';
import { getConfigImageSizeByAreaType } from 'features/EatingWay/utils';
import { getImageMeta } from 'features/SaleType/utils';
import { appActions } from 'features/common/appSlice';
import { selectIsEditMode } from 'features/common/editModeSlice';
import masterDataService from 'features/common/masterDataService';
import { MenuBookProperty } from 'features/common/models';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Accordion, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { EATING_WAY_AREA_IMAGE_TYPE_MAP, displayError, handleServiceError } from 'utils';
import './style.scss';

interface FormSearchValueProps {
  name: string;
}

export interface SelectEatingWayModalProps {
  saleTypeId: number;
  areaType: number;
  position: number;
  exceptEatingWayIds: number[];
  timeFrameId: number;
  onClose?: () => void;
  onReflect?: (returnData: EatingWayListResponse, position: number) => void;
}

function SelectEatingWayModal({
  saleTypeId,
  areaType,
  position,
  exceptEatingWayIds,
  timeFrameId,
  onClose,
  onReflect,
}: SelectEatingWayModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showGlobalModal } = useGlobalModal();
  const configImageSize = getConfigImageSizeByAreaType(areaType);

  // Selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  //Ref
  const formSearchRef = useRef<HTMLFormElement>(null);
  const buttonReflectRef = useRef<HTMLButtonElement>(null);
  const searchNameInputRef = useRef<HTMLInputElement>(null);

  //State
  const [eatingWayDetailModalData, setEatingWayDetailModalData] = useState<EatingWayDetailModalProps>();
  const [showEatingWayDetailModal, setShowEatingWayDetailModal] = useState(false);

  const [eatingWayAreaImageModalData, setEatingWayAreaImageModalData] = useState<EatingWayAreaImageModalProps>();
  const [showEatingWayAreaImageModal, setShowEatingWayAreaImageModal] = useState(false);

  const [propertySource, setPropertySource] = useState<{ [key: number]: string }>();
  const [eatingWayList, setEatingWayList] = useState<EatingWayListResponse[]>([]);
  const [formSearchValue, setFormSearchValue] = useState<FormSearchValueProps>({
    name: '',
  });
  const [filter, setFilter] = useState<EatingWayGetAllPayload>({
    displayType: 2,
    name: '',
    sale_type_id: saleTypeId,
    area_type: areaType,
    except_eating_way_ids: exceptEatingWayIds,
    time_frame_id: timeFrameId,
  });

  function loadEatingWayList() {
    (async () => {
      try {
        const response = await eatingWayService.getAll(filter);
        setEatingWayList(response.data);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function loadPropertyDataSource() {
    //load data for property source
    (async () => {
      try {
        const response = await masterDataService.getMenuBookProperty();
        const propertyMap = response.data.menu_book_property.reduce(
          (menuBookPropertyMap: { [key: number]: string }, menuBookProperty: MenuBookProperty) => {
            menuBookPropertyMap[menuBookProperty.id] = menuBookProperty.name;
            return menuBookPropertyMap;
          },
          {}
        );
        setPropertySource(propertyMap);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  useEffect(() => {
    loadPropertyDataSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadEatingWayList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  function handleClose() {
    onClose?.();
  }

  function handleCollapsedFormSearch() {
    if (formSearchRef.current) {
      const formValue: FormSearchValueProps = {
        name: formSearchRef.current['search_name'].value.trim(),
      };
      setFormSearchValue(formValue);
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (searchNameInputRef.current) {
      setFilter({ ...filter, name: searchNameInputRef.current.value.trim() });
    }
  }

  function handleOpenEatingWayDetailModal(eatingWayId?: number) {
    setEatingWayDetailModalData({
      eatingWayId: eatingWayId,
    });
    setShowEatingWayDetailModal(true);
  }

  function handleCloseEatingWayDetailModal(isDataChanged: boolean) {
    setEatingWayDetailModalData(undefined);
    setShowEatingWayDetailModal(false);

    if (isDataChanged) {
      loadEatingWayList();
    }
  }

  // EatingWayAreaImageModal events
  // area 1
  function handleOpenEatingWayAreaImageModal(eatingWay: EatingWayListResponse) {
    let data: EatingWayAreaImageModalProps = {
      formType: 'update',
      eatingWayId: eatingWay.id,
      eatingWayAreaImage: eatingWay.eating_way_area_images?.[0],
      eatingWayName: eatingWay.name,
      propertyName: propertySource?.[eatingWay.property || 0] ?? '',
      areaType: areaType,
    };
    setEatingWayAreaImageModalData(data);
    setShowEatingWayAreaImageModal(true);
  }

  function handleCloseEatingWayAreaImageModal(isDataChanged: boolean = false) {
    setEatingWayAreaImageModalData(undefined);
    setShowEatingWayAreaImageModal(false);

    if (isDataChanged) {
      loadEatingWayList();
    }
  }
  // end EatingWayAreaImageModal events

  function handleSelectEatingWay() {
    const rdbElement = document.querySelector<HTMLInputElement>('input[name="rdb-select-eating-way"]:checked');
    if (rdbElement) {
      const eatingWayId = Number(rdbElement.value);
      const selectedEatingWay = eatingWayList.find((eatingWay: EatingWayListResponse) => eatingWay.id === eatingWayId);

      if (selectedEatingWay) {
        // check area images (img_ja, img_en) exists and size of image is correct with configured image size
        const eatingWayAreaImage = selectedEatingWay.eating_way_area_images?.[0];
        if (!eatingWayAreaImage) {
          showGlobalModal(() => (
            <AlertModal
              title={t('App.error')}
              message={t('SelectEatingWayModal.warn_image_required')}
              onClose={() => {
                handleOpenEatingWayAreaImageModal(selectedEatingWay);
              }}
            />
          ));
          return;
        } else {
          if (
            eatingWayAreaImage.image_ja === null ||
            (eatingWayAreaImage.image_ja && eatingWayAreaImage.image_ja.length === 0) ||
            eatingWayAreaImage.image_en === null ||
            (eatingWayAreaImage.image_en && eatingWayAreaImage.image_en.length === 0)
          ) {
            showGlobalModal(() => (
              <AlertModal
                title={t('App.error')}
                message={t('SelectEatingWayModal.warn_image_required')}
                onClose={() => {
                  handleOpenEatingWayAreaImageModal(selectedEatingWay);
                }}
              />
            ));
            return;
          }
        }

        // check image size
        // const imageJa = eatingWayAreaImage['image_vi'];
        // const imageEn = eatingWayAreaImage['image_en'] as string;

        const sourceImages = [
          eatingWayAreaImage.image_ja,
          eatingWayAreaImage.image_en,
          eatingWayAreaImage.image_zh_hans,
          eatingWayAreaImage.image_zh_hant,
          eatingWayAreaImage.image_vi,
        ];

        const checkImagePromises: Promise<unknown>[] = [];
        sourceImages.forEach((imagePath) => {
          if (imagePath) {
            const checkImagePromise = new Promise((resolve, reject) => {
              if (imagePath === null || imagePath.length === 0) {
                resolve(false);
                return;
              }

              const url = process.env.REACT_APP_IMAGE_URL + imagePath;
              getImageMeta(url, (img, err) => {
                if (err) {
                  resolve(false);
                }
                if (img) {
                  if (
                    img.naturalWidth >= configImageSize.minWidth &&
                    img.naturalWidth <= configImageSize.maxWidth &&
                    img.naturalHeight >= configImageSize.minHeight &&
                    img.naturalHeight <= configImageSize.maxHeight
                  ) {
                    resolve(true);
                  } else {
                    resolve(false);
                  }
                }
              });
            });

            checkImagePromises.push(checkImagePromise);
          }
        });

        dispatch(appActions.showAppLoading());
        Promise.all(checkImagePromises).then(function (results) {
          const languageKeyErrors: string[] = [];
          results.forEach((result, index) => {
            if (!result) {
              if (index === 0) {
                languageKeyErrors.push(t('SelectEatingWayModal.japanese'));
              }
              if (index === 1) {
                languageKeyErrors.push(t('SelectEatingWayModal.english'));
              }
              if (index === 2) {
                languageKeyErrors.push(t('SelectEatingWayModal.chinese_simplified'));
              }
              if (index === 3) {
                languageKeyErrors.push(t('SelectEatingWayModal.chinese_traditional'));
              }
              if (index === 4) {
                languageKeyErrors.push(t('SelectEatingWayModal.korean'));
              }
              if (index === 5) {
                languageKeyErrors.push(t('SelectEatingWayModal.vietnamese'));
              }
            }
          });
          if (languageKeyErrors.length > 0) {
            let errorMessage = t('SelectEatingWayModal.warn_image_size');
            errorMessage = errorMessage.replace('{0}', languageKeyErrors.join(', '));
            const warnMinSize = t('ImageUploadModal.warn_upload_min_size').replace(
              '{0} x {1}',
              `${configImageSize.minWidth} x ${configImageSize.minHeight}`
            );
            const warnMaxSize = t('ImageUploadModal.warn_upload_max_size').replace(
              '{0} x {1}',
              `${configImageSize.maxWidth} x ${configImageSize.maxHeight}`
            );
            errorMessage = errorMessage.replace('{1}', warnMinSize);
            errorMessage = errorMessage.replace('{2}', warnMaxSize);

            showGlobalModal(() => <AlertModal title={t('App.error')} message={errorMessage} />);
          } else {
            //todo reflect eatingway and close modal
            onReflect?.(selectedEatingWay, position);
            onClose?.();
          }
          dispatch(appActions.hideAppLoading());
        });
      }
    }
  }

  // compose isLoading status
  let isLoading = true;
  if (propertySource && eatingWayList) {
    isLoading = false;
  }

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Modal show size="lg" dialogClassName="select-eating-way-modal" backdrop="static" onHide={handleClose} centered>
        <Modal.Header closeButton className={`${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
          <FeatureTitleBar title={t('SelectEatingWayModal.head_title')} />
        </Modal.Header>
        <Modal.Body className={`${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
          <form ref={formSearchRef} className="mb-3" onSubmit={handleSubmit}>
            <Accordion defaultActiveKey="0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>{t('SelectEatingWayModal.search')}</Accordion.Header>
                <Accordion.Body onExited={handleCollapsedFormSearch}>
                  <Row className="mb-3">
                    <Col sm={'auto'}>
                      <Form.Label htmlFor="txt-store-code" className="col-form-label">
                        {t('SelectEatingWayModal.eating_way_name')}：
                      </Form.Label>
                    </Col>
                    <Col sm={4}>
                      <Form.Control ref={searchNameInputRef} id="txt-name" name="search_name" />
                    </Col>
                    <Col sm={4}>
                      <Button type="submit">{t('SelectEatingWayModal.search')}</Button>
                    </Col>
                  </Row>
                </Accordion.Body>
                <div className="accordion-body accordion-summary">
                  <Row>
                    <Col sm={'auto'}>
                      <Form.Label className="col-form-label">{t('SelectEatingWayModal.eating_way_name')}：</Form.Label>
                      <Form.Label className="col-form-label">{formSearchValue.name || '-'}</Form.Label>
                    </Col>
                  </Row>
                </div>
              </Accordion.Item>
            </Accordion>
          </form>
          <div className="text-end">
            <Button variant="link" onClick={handleOpenEatingWayDetailModal.bind(null, undefined)}>
              {t('SelectEatingWayModal.no_data_guide')}
            </Button>
          </div>
          <div className="d-flex justify-content-end align-items-center mt-2 mb-2">
            <Button ref={buttonReflectRef} className="btn--dark-green" onClick={handleSelectEatingWay}>
              {t('SelectEatingWayModal.selection')}
            </Button>
          </div>

          <div className="sticky-table flex-grow-1 flex-shrink-1" style={{ maxHeight: '499px' }}>
            <Table hover>
              <thead>
                <tr>
                  <th rowSpan={2} style={{ width: '5%' }}>
                    {t('SelectEatingWayModal.selection')}
                  </th>
                  <th rowSpan={2} style={{ width: '15%' }}>
                    {t('SelectEatingWayModal.eating_way')}
                  </th>
                  <th colSpan={6} style={{ width: '54%' }}>
                    {EATING_WAY_AREA_IMAGE_TYPE_MAP[areaType]}
                  </th>
                  <th rowSpan={2} style={{ width: '6%' }}>
                    {t('SelectEatingWayModal.change')}
                  </th>
                  <th rowSpan={2} style={{ width: '20%' }}>
                    {t('SelectEatingWayModal.selected_menu_book')}
                  </th>
                </tr>
                <tr>
                  <th style={{ width: '9%' }}>{t('SelectEatingWayModal.japanese')}</th>
                  <th style={{ width: '9%' }}>{t('SelectEatingWayModal.english')}</th>
                  <th style={{ width: '9%' }}>{t('SelectEatingWayModal.chinese_simplified')}</th>
                  <th style={{ width: '9%' }}>{t('SelectEatingWayModal.chinese_traditional')}</th>
                  <th style={{ width: '9%' }}>{t('SelectEatingWayModal.korean')}</th>
                  <th style={{ width: '9%' }}>{t('SelectEatingWayModal.vietnamese')}</th>
                </tr>
              </thead>
              <tbody>
                {eatingWayList.map((item) => {
                  const area_image = item.eating_way_area_images?.[0];
                  const selectedMenuBookCourse: string[] = item.eating_way_settings.map(function (eatingWaySetting) {
                    if (eatingWaySetting.course) {
                      return eatingWaySetting.menu_book.name + ' ' + eatingWaySetting.course.name;
                    }

                    return eatingWaySetting.menu_book.name;
                  });

                  return (
                    <tr key={item.id}>
                      <td className="text-center">
                        <Form.Check
                          className="chk-eating-way-selection custom-checkbox"
                          type="radio"
                          name="rdb-select-eating-way"
                          value={item.id}
                        ></Form.Check>
                      </td>
                      <td className="text-center">
                        <Button variant="link" onClick={handleOpenEatingWayDetailModal.bind(null, item.id)}>
                          {item.name}
                        </Button>
                      </td>
                      <td className="text-center">
                        {area_image && area_image.image_ja && area_image.image_ja.length > 0 && (
                          <img
                            className="areaimage-preview"
                            src={process.env.REACT_APP_IMAGE_URL + area_image.image_ja}
                            alt=""
                          />
                        )}
                      </td>
                      <td className="text-center">
                        {area_image && area_image.image_en && area_image.image_en.length > 0 && (
                          <img
                            className="areaimage-preview"
                            src={process.env.REACT_APP_IMAGE_URL + area_image.image_en}
                            alt=""
                          />
                        )}
                      </td>
                      <td className="text-center">
                        {area_image && area_image.image_zh_hans && area_image.image_zh_hans.length > 0 && (
                          <img
                            className="areaimage-preview"
                            src={process.env.REACT_APP_IMAGE_URL + area_image.image_zh_hans}
                            alt=""
                          />
                        )}
                      </td>
                      <td className="text-center">
                        {area_image && area_image.image_zh_hant && area_image.image_zh_hant.length > 0 && (
                          <img
                            className="areaimage-preview"
                            src={process.env.REACT_APP_IMAGE_URL + area_image.image_zh_hant}
                            alt=""
                          />
                        )}
                      </td>
                      <td className="text-center">
                        {area_image && area_image.image_ko && area_image.image_ko.length > 0 && (
                          <img
                            className="areaimage-preview"
                            src={process.env.REACT_APP_IMAGE_URL + area_image.image_ko}
                            alt=""
                          />
                        )}
                      </td>
                      <td className="text-center">
                        {area_image && area_image.image_vi && area_image.image_vi.length > 0 && (
                          <img
                            className="areaimage-preview"
                            src={process.env.REACT_APP_IMAGE_URL + area_image.image_vi}
                            alt=""
                          />
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          className="btn--dark-green"
                          onClick={handleOpenEatingWayAreaImageModal.bind(null, item)}
                        >
                          {t('SelectEatingWayModal.change')}
                        </Button>
                      </td>
                      <td className="text-center wrap">{selectedMenuBookCourse.join(', ')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {showEatingWayDetailModal &&
        eatingWayDetailModalData &&
        createPortal(
          <EatingWayDetailModal
            eatingWayId={eatingWayDetailModalData.eatingWayId}
            onClose={handleCloseEatingWayDetailModal}
          />,
          document.body
        )}

      {showEatingWayAreaImageModal &&
        eatingWayAreaImageModalData &&
        createPortal(
          <EatingWayAreaImageModal {...eatingWayAreaImageModalData} onClose={handleCloseEatingWayAreaImageModal} />,
          document.body
        )}
    </>
  );
}

export default SelectEatingWayModal;
